'use client'

import { useCallback, useEffect, useRef } from 'react'

import { useCartStore } from 'modules/cart'
import { noop } from 'shared/utils/common'
import { LocalStorage } from 'shared/utils/storage'

import { SaleOrder } from '../../../types'
import { EditSaleOrderCartKeys } from '../../editSaleOrder/types'
import { useCheckoutPaymentStore } from '../../payment/store/checkoutPaymentStore'
import { PaymentCodes , PaymentMethod } from '../../payment/types'
import { isOnlineTransferMethod } from '../../payment/utils'
import { validateBlikFormValues } from '../../payment/validation'

export const useCheckoutPaymentForm = (paymentMethods: PaymentMethod[]) => {
  const blikCode = useCheckoutPaymentStore(state => state.blikCode)
  const blikValid = useCheckoutPaymentStore(state => state.blikValid)
  const paymentMethod = useCheckoutPaymentStore(state => state.paymentMethodId)
  const paymentMethodName = useCheckoutPaymentStore(
    state => state.paymentMethodName
  )
  const rulesAccepted = useCheckoutPaymentStore(state => state.rulesAccepted)
  const marketingConsents = useCheckoutPaymentStore(
    state => state.marketingConsents
  )
  const handleSetPaymentMethod = useCheckoutPaymentStore(
    state => state.selectPaymentMethod
  )
  const setBlikCode = useCheckoutPaymentStore(state => state.setBlikCode)
  const setBlikValid = useCheckoutPaymentStore(state => state.setBlikValid)
  const setRulesAccepted = useCheckoutPaymentStore(
    state => state.setRulesAccepted
  )
  const setMarketingConsents = useCheckoutPaymentStore(
    state => state.setMarketingConsents
  )
  const resetForm = useCheckoutPaymentStore(state => state.resetForm)

  const refreshCart = useCartStore(state => state.refreshCart)

  const initialPaymentMethodsRef = useRef(paymentMethods)

  const handleBlikCodeChange = useCallback(
    (_: string, value: string) => {
      setBlikCode(value)
    },
    [setBlikCode]
  )

  // Validate BLIK code when it changes
  useEffect(() => {
    validateBlikFormValues({ blikCode }, setBlikValid)
  }, [blikCode, setBlikValid])

  // Initialize with data from previous order if editing
  useEffect(() => {
    refreshCart().catch(noop)

    try {
      const startSaleOrder = new LocalStorage(
        EditSaleOrderCartKeys.EDIT_MODE_START_ORDER
      ).get<null | SaleOrder>(null)

      if (startSaleOrder) {
        const editedPaymentMethod =
          initialPaymentMethodsRef.current.find(
            method => method.id === startSaleOrder.paymentMethodId
          ) ??
          (startSaleOrder.paymentMethod?.code !== PaymentCodes.ON_DELIVERY
            ? initialPaymentMethodsRef.current.find(isOnlineTransferMethod)
            : undefined)

        editedPaymentMethod && handleSetPaymentMethod(editedPaymentMethod)
      }
    } catch (error) {
      console.error('Error parsing edit order data:', error)
    }
  }, [refreshCart, handleSetPaymentMethod])

  useEffect(() => resetForm, [resetForm])

  return {
    blikCode,
    blikValid,
    paymentMethod,
    paymentMethodName,
    rulesAccepted,
    marketingConsents,
    handleBlikCodeChange,
    handleSetPaymentMethod,
    setRulesAccepted,
    setMarketingConsents
  }
}
